/**
 * Bookmark Manager - Manage favorite astronomical objects
 *
 * Provides functionality to:
 * - Bookmark favorite objects
 * - Add notes and priority ratings
 * - Filter and search bookmarked objects
 * - Export bookmark lists
 */

export class BookmarkManager {
    constructor() {
        this.storageKey = 'astro_bookmarked_objects';
    }

    /**
     * Bookmark an object
     * @param {string} objectName - Object name or designation
     * @param {Object} metadata - Optional metadata (notes, priority, etc.)
     * @returns {boolean} Success status
     */
    bookmarkObject(objectName, metadata = {}) {
        try {
            const bookmarks = this.getBookmarks();

            bookmarks[objectName] = {
                name: objectName,
                notes: metadata.notes || '',
                priority: metadata.priority || 3, // 1-5 scale
                tags: metadata.tags || [],
                type: metadata.type || '',
                magnitude: metadata.magnitude || null,
                ra: metadata.ra || null,
                dec: metadata.dec || null,
                bookmarkedDate: new Date().toISOString(),
                lastViewed: metadata.lastViewed || null,
                imagingHistory: metadata.imagingHistory || []
            };

            localStorage.setItem(this.storageKey, JSON.stringify(bookmarks));
            console.log(`Object "${objectName}" bookmarked successfully`);
            return true;
        } catch (error) {
            console.error('Failed to bookmark object:', error);
            return false;
        }
    }

    /**
     * Remove bookmark
     * @param {string} objectName - Object name
     * @returns {boolean} Success status
     */
    removeBookmark(objectName) {
        try {
            const bookmarks = this.getBookmarks();

            if (bookmarks[objectName]) {
                delete bookmarks[objectName];
                localStorage.setItem(this.storageKey, JSON.stringify(bookmarks));
                console.log(`Bookmark for "${objectName}" removed`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to remove bookmark:', error);
            return false;
        }
    }

    /**
     * Check if object is bookmarked
     * @param {string} objectName - Object name
     * @returns {boolean} Is bookmarked
     */
    isBookmarked(objectName) {
        const bookmarks = this.getBookmarks();
        return objectName in bookmarks;
    }

    /**
     * Get all bookmarks
     * @returns {Object} All bookmarks
     */
    getBookmarks() {
        try {
            const saved = localStorage.getItem(this.storageKey);
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Failed to load bookmarks:', error);
            return {};
        }
    }

    /**
     * Get bookmark for specific object
     * @param {string} objectName - Object name
     * @returns {Object|null} Bookmark data or null
     */
    getBookmark(objectName) {
        const bookmarks = this.getBookmarks();
        return bookmarks[objectName] || null;
    }

    /**
     * Update bookmark metadata
     * @param {string} objectName - Object name
     * @param {Object} updates - Fields to update
     * @returns {boolean} Success status
     */
    updateBookmark(objectName, updates) {
        try {
            const bookmarks = this.getBookmarks();

            if (bookmarks[objectName]) {
                bookmarks[objectName] = {
                    ...bookmarks[objectName],
                    ...updates
                };
                localStorage.setItem(this.storageKey, JSON.stringify(bookmarks));
                console.log(`Bookmark for "${objectName}" updated`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to update bookmark:', error);
            return false;
        }
    }

    /**
     * Set priority for bookmarked object
     * @param {string} objectName - Object name
     * @param {number} priority - Priority (1-5, 5 being highest)
     * @returns {boolean} Success status
     */
    setPriority(objectName, priority) {
        if (priority < 1 || priority > 5) {
            console.error('Priority must be between 1 and 5');
            return false;
        }
        return this.updateBookmark(objectName, { priority });
    }

    /**
     * Add note to bookmark
     * @param {string} objectName - Object name
     * @param {string} note - Note text
     * @returns {boolean} Success status
     */
    addNote(objectName, note) {
        return this.updateBookmark(objectName, { notes: note });
    }

    /**
     * Add tag to bookmark
     * @param {string} objectName - Object name
     * @param {string} tag - Tag to add
     * @returns {boolean} Success status
     */
    addTag(objectName, tag) {
        const bookmark = this.getBookmark(objectName);
        if (!bookmark) return false;

        const tags = bookmark.tags || [];
        if (!tags.includes(tag)) {
            tags.push(tag);
            return this.updateBookmark(objectName, { tags });
        }
        return true;
    }

    /**
     * Remove tag from bookmark
     * @param {string} objectName - Object name
     * @param {string} tag - Tag to remove
     * @returns {boolean} Success status
     */
    removeTag(objectName, tag) {
        const bookmark = this.getBookmark(objectName);
        if (!bookmark) return false;

        const tags = (bookmark.tags || []).filter(t => t !== tag);
        return this.updateBookmark(objectName, { tags });
    }

    /**
     * Get bookmarks sorted by priority
     * @returns {Array} Sorted bookmarks
     */
    getBookmarksByPriority() {
        const bookmarks = this.getBookmarks();
        return Object.values(bookmarks).sort((a, b) => b.priority - a.priority);
    }

    /**
     * Get bookmarks by tag
     * @param {string} tag - Tag to filter by
     * @returns {Array} Filtered bookmarks
     */
    getBookmarksByTag(tag) {
        const bookmarks = this.getBookmarks();
        return Object.values(bookmarks).filter(b =>
            (b.tags || []).includes(tag)
        );
    }

    /**
     * Get all unique tags
     * @returns {Array<string>} Unique tags
     */
    getAllTags() {
        const bookmarks = this.getBookmarks();
        const tagSet = new Set();

        Object.values(bookmarks).forEach(bookmark => {
            (bookmark.tags || []).forEach(tag => tagSet.add(tag));
        });

        return Array.from(tagSet).sort();
    }

    /**
     * Search bookmarks
     * @param {string} query - Search query
     * @returns {Array} Matching bookmarks
     */
    searchBookmarks(query) {
        const bookmarks = this.getBookmarks();
        const lowerQuery = query.toLowerCase();

        return Object.values(bookmarks).filter(bookmark => {
            const nameMatch = bookmark.name.toLowerCase().includes(lowerQuery);
            const notesMatch = (bookmark.notes || '').toLowerCase().includes(lowerQuery);
            const tagsMatch = (bookmark.tags || []).some(tag =>
                tag.toLowerCase().includes(lowerQuery)
            );

            return nameMatch || notesMatch || tagsMatch;
        });
    }

    /**
     * Export bookmarks to JSON
     * @returns {boolean} Success status
     */
    exportBookmarks() {
        try {
            const bookmarks = this.getBookmarks();

            const dataStr = JSON.stringify(bookmarks, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `astro-bookmarks-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('Bookmarks exported successfully');
            return true;
        } catch (error) {
            console.error('Failed to export bookmarks:', error);
            return false;
        }
    }

    /**
     * Export bookmarks to CSV
     * @returns {boolean} Success status
     */
    exportBookmarksCSV() {
        try {
            const bookmarks = Object.values(this.getBookmarks());

            let csv = 'Name,Type,Magnitude,RA,Dec,Priority,Notes,Tags,Bookmarked Date\n';

            bookmarks.forEach(bookmark => {
                const row = [
                    bookmark.name,
                    bookmark.type || '',
                    bookmark.magnitude || '',
                    bookmark.ra || '',
                    bookmark.dec || '',
                    bookmark.priority,
                    `"${(bookmark.notes || '').replace(/"/g, '""')}"`,
                    `"${(bookmark.tags || []).join(', ')}"`,
                    bookmark.bookmarkedDate
                ].join(',');
                csv += row + '\n';
            });

            const dataBlob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(dataBlob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `astro-bookmarks-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('Bookmarks exported to CSV successfully');
            return true;
        } catch (error) {
            console.error('Failed to export bookmarks to CSV:', error);
            return false;
        }
    }

    /**
     * Import bookmarks from JSON
     * @param {File} file - JSON file to import
     * @returns {Promise<boolean>} Success status
     */
    async importBookmarks(file) {
        try {
            const text = await file.text();
            const importedBookmarks = JSON.parse(text);

            const currentBookmarks = this.getBookmarks();
            const mergedBookmarks = { ...currentBookmarks, ...importedBookmarks };

            localStorage.setItem(this.storageKey, JSON.stringify(mergedBookmarks));
            console.log('Bookmarks imported successfully');
            return true;
        } catch (error) {
            console.error('Failed to import bookmarks:', error);
            return false;
        }
    }

    /**
     * Record imaging session for object
     * @param {string} objectName - Object name
     * @param {Object} sessionData - Session information
     * @returns {boolean} Success status
     */
    recordImagingSession(objectName, sessionData) {
        const bookmark = this.getBookmark(objectName);
        if (!bookmark) return false;

        const history = bookmark.imagingHistory || [];
        history.push({
            date: sessionData.date || new Date().toISOString(),
            duration: sessionData.duration || 0,
            filters: sessionData.filters || [],
            exposures: sessionData.exposures || 0,
            notes: sessionData.notes || ''
        });

        return this.updateBookmark(objectName, {
            imagingHistory: history,
            lastViewed: new Date().toISOString()
        });
    }

    /**
     * Get bookmark statistics
     * @returns {Object} Statistics
     */
    getStatistics() {
        const bookmarks = Object.values(this.getBookmarks());

        const stats = {
            total: bookmarks.length,
            byPriority: {
                high: bookmarks.filter(b => b.priority >= 4).length,
                medium: bookmarks.filter(b => b.priority === 3).length,
                low: bookmarks.filter(b => b.priority <= 2).length
            },
            byType: {},
            totalTags: this.getAllTags().length,
            withNotes: bookmarks.filter(b => b.notes && b.notes.length > 0).length,
            withImagingHistory: bookmarks.filter(b =>
                b.imagingHistory && b.imagingHistory.length > 0
            ).length
        };

        // Count by type
        bookmarks.forEach(bookmark => {
            const type = bookmark.type || 'Unknown';
            stats.byType[type] = (stats.byType[type] || 0) + 1;
        });

        return stats;
    }

    /**
     * Clear all bookmarks
     * @returns {boolean} Success status
     */
    clearAllBookmarks() {
        try {
            localStorage.removeItem(this.storageKey);
            console.log('All bookmarks cleared');
            return true;
        } catch (error) {
            console.error('Failed to clear bookmarks:', error);
            return false;
        }
    }

    /**
     * Get bookmark count
     * @returns {number} Number of bookmarks
     */
    getBookmarkCount() {
        return Object.keys(this.getBookmarks()).length;
    }
}

export default BookmarkManager;
